import React, { useCallback, useState } from "react";
import { faGripDotsVertical } from "@fortawesome/pro-solid-svg-icons";
import {
  faTrash,
  faEdit,
  faSave,
  faTimes,
  faExclamationTriangle,
} from "@fortawesome/pro-regular-svg-icons";
import Button from "../../../buttons/Button";
import Alert from "../../../utils/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  updateCategoriesOrder,
  deleteCategory,
  updateCategory,
} from "../../../../services/dishService";
import { toast } from "../../../../utils/demoToast";
import { useSelector } from "react-redux";

// Sortable wrapper for category items
const SortableCategoryRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

const CategoryManager = ({
  types = [],
  initialCategories = [],
  tKey = "",
  updateCategories = () => {},
}) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState(
    [...initialCategories].sort((a, b) => a.order - b.order),
  );
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const restaurantId = useSelector((state) => state.user.restaurantId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleUpdateCategoriesOfParent = useCallback(
    (newCategories) => {
      updateCategories(newCategories);
    },
    [updateCategories],
  );

  const handleDragEnd = useCallback(
    (event, typeId) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const items = categories.filter((c) => c.dishTypeId === typeId);
      const other = categories.filter((c) => c.dishTypeId !== typeId);

      const oldIndex = items.findIndex(
        (c) => String(c.dishCategoryId) === String(active.id),
      );
      const newIndex = items.findIndex(
        (c) => String(c.dishCategoryId) === String(over.id),
      );
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const reordered = Array.from(items);
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      const newList = [...other, ...reordered];

      try {
        updateCategoriesOrder(
          restaurantId,
          reordered.map((c) => c.dishCategoryId),
        );
        setCategories(newList);
        handleUpdateCategoriesOfParent(newList);
        setEditId(null);
        setEditValue("");
        toast.success(t(`${tKey}categoriesManagement.updateOrderSuccess`));
      } catch (error) {
        console.error("Error updating categories order:", error);
        toast.error(error.message);
      }
    },
    [categories, t, tKey, restaurantId, handleUpdateCategoriesOfParent],
  );

  const handleUpdateCategory = useCallback(
    async (categoryId) => {
      const category = categories.find((c) => c.dishCategoryId === categoryId);
      if (!category) return;

      try {
        await updateCategory(categoryId, editValue);
        const updatedCategory = {
          ...category,
          label: editValue,
        };
        setCategories((prev) => {
          const updated = prev.map((c) =>
            c.dishCategoryId === categoryId ? updatedCategory : c,
          );
          handleUpdateCategoriesOfParent(updated);
          return updated;
        });
        toast.success(t(`${tKey}categoriesManagement.updateSuccess`));
      } catch (error) {
        console.error("Error updating category:", error);
        toast.error(error.message);
      }
    },
    [categories, editValue, t, tKey, handleUpdateCategoriesOfParent],
  );

  const handleDeleteCategory = useCallback(
    async (categoryId) => {
      const category = categories.find((c) => c.dishCategoryId === categoryId);
      if (!category) return;

      try {
        await deleteCategory(categoryId);
        setCategories((prev) => {
          const updated = prev.filter((c) => c.dishCategoryId !== categoryId);
          handleUpdateCategoriesOfParent(updated);
          return updated;
        });
        toast.success(t(`${tKey}categoriesManagement.deleteSuccess`));
      } catch (error) {
        console.error("Error deleting category:", error);
        toast.error(error.message);
      }
    },
    [categories, t, tKey, handleUpdateCategoriesOfParent],
  );

  return (
    <>
      <h3 className="mt-0 mb-3">{t(`${tKey}categoriesManagement.title`)}</h3>
      <Alert variant="pastel-danger" className="flex gap-2 mb-3 align-center">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
        <p className="italic">{t(`${tKey}categoriesManagement.description`)}</p>
      </Alert>
      {types.slice().map((type) => {
        const typeId = type.dishTypeId;
        const related = categories.filter((c) => c.dishTypeId === typeId);
        if (related.length === 0) return null;

        const itemIds = related.map((c) => String(c.dishCategoryId));

        return (
          <section key={typeId} className="mb-3">
            <p className="h5 mt-0 mb-2">
              {t(`utils.dishTypes.${type.label}`)}
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleDragEnd(event, typeId)}
            >
              <SortableContext
                items={itemIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex column gap-1">
                  {related.map((category) => (
                    <SortableCategoryRow
                      key={category.dishCategoryId}
                      id={String(category.dishCategoryId)}
                    >
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <div
                          ref={setNodeRef}
                          style={style}
                          className="flex justify-between items-center"
                        >
                          <div className="flex items-center align-center gap-1">
                            <span
                              {...attributes}
                              {...listeners}
                              className="cursor-move"
                            >
                              <FontAwesomeIcon icon={faGripDotsVertical} />
                            </span>

                            {editId === category.dishCategoryId ? (
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="border p-2 bg-pastel-medium"
                              />
                            ) : (
                              <span className="p-2">{category.label}</span>
                            )}
                          </div>

                          <div className="flex items-center align-center gap-1">
                            {editId === category.dishCategoryId ? (
                              <>
                                <Button
                                  icon
                                  variant="success"
                                  onClick={() => {
                                    handleUpdateCategory(
                                      category.dishCategoryId,
                                    );
                                    setEditId(null);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faSave} />
                                </Button>

                                <Button
                                  icon
                                  variant="secondary"
                                  onClick={() => setEditId(null)}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </Button>
                              </>
                            ) : (
                              <Button
                                icon
                                variant="info"
                                onClick={() => {
                                  setEditId(category.dishCategoryId);
                                  setEditValue(category.label);
                                }}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                            )}

                            {deleteConfirmId === category.dishCategoryId ? (
                              <Button
                                variant="danger"
                                size="xs"
                                onClick={() => {
                                  handleDeleteCategory(
                                    category.dishCategoryId,
                                  );
                                  setDeleteConfirmId(null);
                                }}
                              >
                                {t(
                                  `${tKey}categoriesManagement.deleteConfirm`,
                                )}
                              </Button>
                            ) : (
                              <Button
                                icon
                                variant="danger"
                                onClick={() =>
                                  setDeleteConfirmId(category.dishCategoryId)
                                }
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </SortableCategoryRow>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </section>
        );
      })}
    </>
  );
};

export default CategoryManager;
